import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Flex, VStack, Text, Button, Divider, Spacer, Icon,
  Avatar, useToast, IconButton, useDisclosure,
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerBody,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiVideo, FiKey, FiUsers, FiActivity, FiFileText, FiLogOut,
  FiSettings, FiBook, FiServer, FiShield, FiMenu, FiRadio,
} from 'react-icons/fi';

import Login from './components/Login';
import VideoRoom from './components/VideoRoom';
import JoinRoom from './components/JoinRoom';
import Rooms from './components/Rooms';
import ApiKeys from './components/ApiKeys';
import Organizations from './components/Organizations';
import ActiveSessions from './components/ActiveSessions';
import AuditLog from './components/AuditLog';
import HealthCheck from './components/HealthCheck';
import ApiDocs from './components/ApiDocs';
import SuperAdmin from './components/SuperAdmin';
import PostgresStatus from './components/PostgresStatus';
import StreamingStatus from './components/StreamingStatus';

const NavButton = ({ to, icon, children, isActive }) => (
  <Button
    as={RouterLink}
    to={to}
    leftIcon={<Icon as={icon} boxSize={5} />}
    width="full"
    justifyContent="flex-start"
    variant={isActive ? 'solid' : 'ghost'}
    colorScheme={isActive ? 'teal' : 'whiteAlpha'}
    color={isActive ? 'white' : 'whiteAlpha.800'}
    bg={isActive ? 'whiteAlpha.200' : 'transparent'}
    _hover={{
      bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
      color: 'white',
      textDecoration: 'none',
    }}
    borderRadius="lg"
    py={6}
    fontSize="md"
    fontWeight={isActive ? '600' : '400'}
  >
    {children}
  </Button>
);

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('meet_token'));
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => { onClose(); }, [location.pathname, onClose]);

  const handleLogin = useCallback((tok) => {
    setToken(tok);
    localStorage.setItem('meet_token', tok);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('meet_token');
    setToken(null);
    navigate('/');
    toast({ title: 'Logged out', status: 'info', duration: 2000 });
  }, [navigate, toast]);

  // Video room routes are public (auth via JWT join token)
  const isVideoRoute = location.pathname.startsWith('/join/') || location.pathname === '/room';

  if (!token && !isVideoRoute) {
    return <Login onLogin={handleLogin} />;
  }

  // Standalone video room (no sidebar)
  if (isVideoRoute) {
    return (
      <Routes>
        <Route path="/join/:roomId" element={<JoinRoom />} />
        <Route path="/room" element={<VideoRoom />} />
      </Routes>
    );
  }

  const renderSidebarContent = () => (
    <>
      {/* Logo / Brand */}
      <Flex align="center" px={4} mb={6}>
        <Box w={12} h={12} borderRadius="full" overflow="hidden" bg="white" p="2px" flexShrink={0}>
          <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </Box>
        <Box ml={3}>
          <Text fontSize="lg" fontWeight="bold" color="white">Selwyn Meet</Text>
        </Box>
      </Flex>

      <Divider borderColor="whiteAlpha.300" mb={2} />

      {/* Meetings */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={1}>
        Meetings
      </Text>
      <NavButton to="/admin/rooms" icon={FiVideo} isActive={location.pathname.startsWith('/admin/rooms')}>
        Rooms
      </NavButton>
      <NavButton to="/admin/sessions" icon={FiActivity} isActive={location.pathname.startsWith('/admin/sessions')}>
        Active Sessions
      </NavButton>
      <NavButton to="/admin/streaming-status" icon={FiRadio} isActive={location.pathname.startsWith('/admin/streaming-status')}>
        Streaming Status
      </NavButton>

      {/* Management */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={4} mb={1}>
        Management
      </Text>
      <NavButton to="/admin/organizations" icon={FiUsers} isActive={location.pathname.startsWith('/admin/organizations')}>
        Organizations
      </NavButton>
      <NavButton to="/admin/api-keys" icon={FiKey} isActive={location.pathname.startsWith('/admin/api-keys')}>
        API Keys
      </NavButton>

      {/* Administration */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={4} mb={1}>
        Administration
      </Text>
      <NavButton to="/admin/super-admin" icon={FiShield} isActive={location.pathname.startsWith('/admin/super-admin')}>
        Super Admin
      </NavButton>
      <NavButton to="/admin/audit-log" icon={FiFileText} isActive={location.pathname.startsWith('/admin/audit-log')}>
        Audit Log
      </NavButton>

      {/* Infrastructure */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={4} mb={1}>
        Infrastructure
      </Text>
      <NavButton to="/admin/postgres" icon={FiServer} isActive={location.pathname.startsWith('/admin/postgres')}>
        PostgreSQL Status
      </NavButton>
      <NavButton to="/admin/health" icon={FiSettings} isActive={location.pathname.startsWith('/admin/health')}>
        Health Check
      </NavButton>

      {/* Developer */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={4} mb={1}>
        Developer
      </Text>
      <NavButton to="/admin/api-docs" icon={FiBook} isActive={location.pathname.startsWith('/admin/api-docs')}>
        API Docs
      </NavButton>

      <Spacer />

      <Divider borderColor="whiteAlpha.300" />
      <Flex align="center" px={4} py={3}>
        <Avatar size="sm" bg="teal.400" />
        <Box ml={3} flex={1}>
          <Text fontSize="sm" color="white" fontWeight="medium">Admin</Text>
          <Text fontSize="xs" color="whiteAlpha.600">Logged in</Text>
        </Box>
      </Flex>
      <Button
        leftIcon={<Icon as={FiLogOut} />}
        onClick={handleLogout}
        width="full"
        justifyContent="flex-start"
        variant="ghost"
        color="whiteAlpha.800"
        _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
        borderRadius="lg"
      >
        Logout
      </Button>
    </>
  );

  return (
    <Flex direction="column" height="100vh" bg="gray.50">
      {/* Mobile top bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        flexShrink={0}
        height="56px"
        bg="teal.700"
        align="center"
        px={4}
        justify="space-between"
        boxShadow="md"
      >
        <IconButton
          icon={<Icon as={FiMenu} />}
          onClick={onOpen}
          variant="ghost"
          color="white"
          aria-label="Open navigation"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
        <Text color="white" fontWeight="bold" fontSize="md">Selwyn Meet</Text>
        <IconButton
          icon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          variant="ghost"
          color="white"
          aria-label="Logout"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Flex>

      {/* Desktop layout row */}
      <Flex flex="1" overflow="hidden">
        {/* Desktop Sidebar */}
        <VStack
          as="nav"
          display={{ base: 'none', md: 'flex' }}
          width="260px"
          py={6}
          px={4}
          bgGradient="linear(to-b, teal.600, teal.900)"
          spacing={2}
          align="stretch"
          boxShadow="xl"
          overflowY="auto"
          flexShrink={0}
        >
          {renderSidebarContent()}
        </VStack>

        {/* Main Content */}
        <Box flex="1" p={{ base: 4, md: 8 }} overflowY="auto">
          <Routes>
            <Route path="/" element={<Rooms />} />
            <Route path="/admin/rooms" element={<Rooms />} />
            <Route path="/admin/sessions" element={<ActiveSessions />} />
            <Route path="/admin/streaming-status" element={<StreamingStatus />} />
            <Route path="/admin/organizations" element={<Organizations />} />
            <Route path="/admin/api-keys" element={<ApiKeys />} />
            <Route path="/admin/super-admin" element={<SuperAdmin />} />
            <Route path="/admin/audit-log" element={<AuditLog />} />
            <Route path="/admin/postgres" element={<PostgresStatus />} />
            <Route path="/admin/health" element={<HealthCheck />} />
            <Route path="/admin/api-docs" element={<ApiDocs />} />
            <Route path="/join/:roomId" element={<JoinRoom />} />
            <Route path="/room" element={<VideoRoom />} />
          </Routes>
        </Box>
      </Flex>

      {/* Mobile nav drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent bgGradient="linear(to-b, teal.600, teal.900)" maxW="280px">
          <DrawerCloseButton color="white" top={4} />
          <DrawerBody p={0}>
            <VStack spacing={2} align="stretch" py={6} px={4} minH="100%">
              {renderSidebarContent()}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}
